// Vendor Portal — Account & Security (change password).
import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";
import vendorApi from "@/lib/vendorApi";
import { useVendor } from "@/pages/vendor/VendorLayout";

const inputCls = "w-full bg-[#FAFAF7] border border-[#E9E2D2] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63]";

export default function VendorAccount() {
  const { me, reload, mustChange } = useVendor();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPw !== confirm) return toast.error("New password and confirmation do not match");
    setBusy(true);
    try {
      await vendorApi.post("/vendor/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password updated");
      setOldPw(""); setNewPw(""); setConfirm("");
      reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5 md:p-8 max-w-3xl mx-auto space-y-5" data-testid="vendor-change-password-card">
      <section className="bg-white border border-[#E9E2D2] rounded-2xl p-6 md:p-8 shadow-sm">
        <div className="flex items-center gap-2 text-[#0D5C63]">
          <KeyRound size={18} />
          <h1 className="trust-serif text-2xl">Change your password</h1>
        </div>
        <p className="text-xs text-[#8a8478] mt-1">
          {mustChange ? "You must change the temporary password emailed to you before you can upload documents or pay." : "Update your password anytime."}
        </p>
        <form onSubmit={submit} className="mt-5 grid md:grid-cols-3 gap-4">
          <label className="block">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-1.5">Current Password *</div>
            <input type="password" className={inputCls} value={oldPw} onChange={(e) => setOldPw(e.target.value)} data-testid="vendor-cp-old" />
          </label>
          <label className="block">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-1.5">New Password *</div>
            <input type="password" className={inputCls} value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="vendor-cp-new" />
          </label>
          <label className="block">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-1.5">Confirm New *</div>
            <input type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="vendor-cp-confirm" />
          </label>
          <div className="md:col-span-3">
            <button type="submit" disabled={busy} data-testid="vendor-cp-submit" className="inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full disabled:opacity-60">
              {busy ? <><Loader2 size={13} className="animate-spin" /> Updating…</> : <><ShieldCheck size={13} /> Update Password</>}
            </button>
          </div>
        </form>
      </section>

      <section className="bg-white border border-[#E9E2D2] rounded-2xl p-5">
        <div className="text-[10px] uppercase tracking-widest text-[#8a8478]">Signed in as</div>
        <div className="text-sm font-semibold text-[#1A1A1A]">{me?.email}</div>
        {me?.last_login_at && (
          <div className="text-[11px] text-[#8a8478] mt-1">Last login: {new Date(me.last_login_at).toLocaleString()}</div>
        )}
      </section>
    </div>
  );
}
